import { Gauge, Layers, Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useOperationsReport, usePeriod } from '../api'
import { ReportCard } from '../components/ReportBits'
import { ReportLayout } from '../components/ReportLayout'

export function OperationsReportPage() {
  const { t } = useTranslation()
  const { months } = usePeriod()
  const q = useOperationsReport(months)
  const r = q.data

  return (
    <ReportLayout name="operasyon" title={t('reports.pages.operations.title')} description={t('reports.pages.operations.subtitle')} query={q}>
      {r ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <ReportCard icon={Gauge} title={t('reports.ops.throughput')} description={t('reports.ops.throughputHint')}>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('reports.col.project')}</TH>
                    <TH>{t('reports.col.status')}</TH>
                    <TH className="text-right">{t('reports.col.tasks')}</TH>
                    <TH className="text-right">{t('reports.col.overdue')}</TH>
                    <TH className="text-right">{t('reports.col.openTasks')}</TH>
                  </TR>
                </THead>
                <TBody>
                  {r.tasks.columns.map((c) => (
                    <TR key={`${c.board}-${c.columnKey}`}>
                      <TD>{c.board}</TD>
                      <TD>{c.columnName || c.columnKey}</TD>
                      <TD className="text-right tabular-nums">{c.tasks}</TD>
                      <TD className="text-right tabular-nums">{c.overdue}</TD>
                      <TD className="text-right tabular-nums">{c.unassigned}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </ReportCard>
            <ReportCard icon={Layers} title={t('reports.ops.workload')} description={t('reports.ops.workloadHint')}>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('reports.col.person')}</TH>
                    <TH className="text-right">{t('reports.col.openTasks')}</TH>
                    <TH className="text-right">{t('reports.col.overdue')}</TH>
                    <TH className="text-right">{t('reports.series.completed')}</TH>
                  </TR>
                </THead>
                <TBody>
                  {r.tasks.assignees.map((a) => (
                    <TR key={a.userId || a.email}>
                      <TD>{a.email}</TD>
                      <TD className="text-right tabular-nums">{a.open}</TD>
                      <TD className="text-right tabular-nums">{a.overdue}</TD>
                      <TD className="text-right tabular-nums">{a.completed30d}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </ReportCard>
          </div>
          <ReportCard icon={Wrench} title={t('reports.ops.installations')} description={t('reports.ops.installationsHint')}>
            <p className="mb-3 text-sm text-muted-foreground">
              {r.installations.licenseStale} · {r.installations.missingInBinary} · {r.installations.silentOver24h}
            </p>
            <Table>
              <THead>
                <TR>
                  <TH>{t('reports.col.status')}</TH>
                  <TH className="text-right">{t('reports.col.count')}</TH>
                </TR>
              </THead>
              <TBody>
                {r.installations.byEnvironmentStatus.map((row) => (
                  <TR key={`${row.environment}-${row.status}`}>
                    <TD>
                      {row.environment} · {row.status}
                    </TD>
                    <TD className="text-right tabular-nums">{row.installations}</TD>
                  </TR>
                ))}
                {r.installations.accountInstallStages.map((row) => (
                  <TR key={row.stage}>
                    <TD>{row.stage}</TD>
                    <TD className="text-right tabular-nums">{row.accounts}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <p className="mt-3 text-sm text-muted-foreground">
              {t('reports.col.conversion')}: %{r.demos.conversionRatePercent} · {r.demos.avgDaysToConvert} / {r.demos.medianDaysToConvert}
            </p>
          </ReportCard>
        </div>
      ) : null}
    </ReportLayout>
  )
}
